"use client"

/**
 * useNetworkStatus Hook
 * Monitors online/offline status and connection quality
 */

import { useEffect, useState } from "react"

interface NetworkStatus {
  online: boolean
  effectiveType: "slow-2g" | "2g" | "3g" | "4g" | "unknown"
  downlink?: number
  rtt?: number
  saveData?: boolean
}

export function useNetworkStatus(): NetworkStatus {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    const connection =
      (navigator as Navigator & { connection?: NetworkInformation }).connection ||
      (navigator as Navigator & { mozConnection?: NetworkInformation }).mozConnection ||
      (navigator as Navigator & { webkitConnection?: NetworkInformation }).webkitConnection

    return {
      online: navigator.onLine,
      effectiveType: (connection?.effectiveType as NetworkStatus["effectiveType"]) || "unknown",
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      saveData: connection?.saveData,
    }
  })

  useEffect(() => {
    const updateOnlineStatus = () => {
      setNetworkStatus((prev) => ({ ...prev, online: navigator.onLine }))
    }

    const updateConnectionStatus = () => {
      const connection =
        (navigator as Navigator & { connection?: NetworkInformation }).connection ||
        (navigator as Navigator & { mozConnection?: NetworkInformation }).mozConnection ||
        (navigator as Navigator & { webkitConnection?: NetworkInformation }).webkitConnection

      if (connection) {
        setNetworkStatus({
          online: navigator.onLine,
          effectiveType: (connection.effectiveType as NetworkStatus["effectiveType"]) || "unknown",
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData,
        })
      }
    }

    window.addEventListener("online", updateOnlineStatus)
    window.addEventListener("offline", updateOnlineStatus)

    const connection =
      (navigator as Navigator & { connection?: NetworkInformation }).connection ||
      (navigator as Navigator & { mozConnection?: NetworkInformation }).mozConnection ||
      (navigator as Navigator & { webkitConnection?: NetworkInformation }).webkitConnection

    if (connection) {
      connection.addEventListener("change", updateConnectionStatus)
    }

    return () => {
      window.removeEventListener("online", updateOnlineStatus)
      window.removeEventListener("offline", updateOnlineStatus)

      if (connection) {
        connection.removeEventListener("change", updateConnectionStatus)
      }
    }
  }, [])

  return networkStatus
}

interface NetworkInformation {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g"
  downlink?: number
  rtt?: number
  saveData?: boolean
  addEventListener: (type: string, listener: () => void) => void
  removeEventListener: (type: string, listener: () => void) => void
}
